import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Chip, ClickAwayListener } from "@mui/material";
import { alpha } from "@mui/material/styles";

import Stack from "components/common/Stack";
import Text from "components/common/Text";
import JarvisButton from "components/JarvisButton";
import Box from "components/common/Box";
import Image from "components/common/Image";
import TooltipHover from "components/common/TooltipHover";

import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import { localStore } from "stores/localStorage";
import { config } from "config";

import { useHistory } from "react-router-dom";

function PhcOverview() {
  const history = useHistory();
  const dispatch = useDispatch();

  // -------------------- Redux State --------------------
  const { currentStage, isPaymentCompleted, name, overviewList } = useSelector(
    (state) => ({
      currentStage: state.PHC.PHCOnboarding.currentStage,
      isPaymentCompleted: state.PHC.PHCOnboarding.isPaymentCompleted,
      name: state.customer.loginProfile.name,
      overviewList: [
        {
          id: 1,
          stageNumber: 1,
          name: "Upload Portfolio",
          toolTipText:
            "Upload or sync your existing portfolio so Jarvis can analyze your current holdings.",
        },
        {
          id: 2,
          stageNumber: 2,
          name: "Preview Portfolio",
          toolTipText:
            "Review all imported stocks and confirm the portfolio before generating insights.",
        },
        {
          id: 3,
          stageNumber: 3,
          name: "Generate Report",
          toolTipText:
            "Jarvis will analyze your portfolio to highlight risks, inefficiencies, and missed gains.",
        },
        {
          id: 4,
          stageNumber: 4,
          name: "CKYC",
          toolTipText:
            "Complete your C-KYC verification to proceed with the analysis process.",
        },
        {
          id: 5,
          stageNumber: 5,
          name: "Save Report",
          toolTipText:
            "Jarvis will analyze your portfolio to highlight risks, inefficiencies, and missed gains.",
        },
      ],
    })
  );

  // -------------------- Local State --------------------
  const [openId, setOpenId] = useState(null);

  const onboardingStages = [
    {
      stageId: ["PORTFOLIO_UPLOAD"],
      stageNumber: 1,
      heading: "Portfolio Upload",
      uri: "/phc/onboarding/portfolio-upload",
      subheading: "Upload or sync your existing portfolio to begin analysis.",
    },
    {
      stageId: ["PREVIEW"],
      stageNumber: 2,
      heading: "Preview Portfolio",
      uri: "/phc/onboarding/preview",
      subheading: "Review the imported stocks before generating insights.",
    },
     {
      stageId: ["GENERATE_PORTFOLIO"],
      stageNumber: 3,
      heading: "Generate Report",
      uri: "/phc/onboarding/generate-portfolio",
      subheading:
        "AI processes your portfolio to identify risks and missed gains.",
    },
    {
      stageId: ["CKYC", "DOCUMENT_SIGNING"],
      stageNumber: 4,
      heading: "CKYC & E-Sign",
      uri: "/phc/onboarding/ckyc",
      subheading: "Complete your verification to proceed with analysis.",
    },
   
    {
      stageId: ["SAVE_REPORT"],
      stageNumber: 5,
      heading: "Generate Report",
      uri: "/phc/onboarding/generate-portfolio",
      subheading:
        "AI processes your portfolio to identify risks and missed gains.",
    },
  ];

  // -------------------- Helpers --------------------
  const getActiveStage = () => {
    if (isPaymentCompleted) {
      return onboardingStages.find((s) => s.stageId.includes("CKYC"));
    }
    return onboardingStages.find((s) => s.stageId.includes(currentStage));
  };

  const activeStage = getActiveStage();

  const handleStageRedirection = () => {
    if (activeStage && activeStage.stageNumber > 0) {
      history.replace(activeStage.uri);
      return;
    }
    history.replace("/phc/onboarding");
  };

  const handleTooltip = (id, type = "open") => {
    setOpenId(type === "open" ? id : null);
  };

  // -------------------- SSO Conditions --------------------
  const ssoDetails = localStore.getSsoCustomerDetails();
  const isCentrumSsoCustomer =
    localStore.isSsoCustomer() &&
    ssoDetails?.broker === config?.broker?.ssoPlatform?.centrum;

  return (
    <>
      {/* Greeting */}
      <Text
        sx={{ fontWeight: 500, fontSize: { xs: "1rem", md: "1.2rem" }, mt: 2 }}
      >
        Hi {name},
      </Text>

      <Text
        sx={{ fontSize: { xs: "0.875rem", md: "1.125rem" }, color: "#676E76" }}
        mb={4}
      >
        Jarvis keeps you ahead of market shifts with an AI-driven PHC
      </Text>

      {/* Title */}
      <Text
        sx={{
          fontSize: { xs: "1rem", md: "2rem" },
          fontWeight: 500,
          lineHeight: { xs: "23px", md: "41px" },
        }}
        color={"#212225"}
        mb={4}
      >
        You are just <span style={{ color: "#00DB99" }}>4 simple steps</span>{" "}
        away from hiring Jarvis.
      </Text>

      {/* Stages */}
      <ClickAwayListener onClickAway={() => handleTooltip("", "close")}>
        <Stack direction={{ xs: "column", md: "row" }}>
          {overviewList.map((item, index) => (
            <Stack key={item.id} direction="row" alignItems="center" mb={2}>
              <Chip
                label={item.id}
                size="small"
                sx={{
                  color:
                    item.stageNumber < activeStage?.stageNumber
                      ? "white"
                      : "primary.dark",
                  backgroundColor:
                    item.stageNumber < activeStage?.stageNumber
                      ? alpha("#2e7d32", 0.8)
                      : alpha("#38a1ff", 0.5),
                  fontWeight: 500,
                  minWidth: "24px",
                }}
              />
              <Text ml={1} sx={{ fontSize: { xs: "0.85rem", md: "1rem" } }}>
                {isCentrumSsoCustomer && item.name?.toLowerCase() === "ckyc"
                  ? "E-Sign"
                  : item.name}
              </Text>

              <TooltipHover
                title={item.toolTipText}
                sx={{ ml: 1, mr: 2, mt: 1 }}
              >
                <InfoRoundedIcon sx={{ width: 20, color: "#a39e9e" }} />
              </TooltipHover>
            </Stack>
          ))}
        </Stack>
      </ClickAwayListener>

      {/* Aadhaar Info */}
      <Text sx={{ display: "flex", alignItems: "center", mt: 2 }}>
        <Image src="/svgs/aadhaar.svg" width={60} />
        <span style={{ marginLeft: 15 }}>
          Keep your Aadhaar {isCentrumSsoCustomer ? "" : " & PAN card "} handy
          for {isCentrumSsoCustomer ? "E-Sign" : "C-KYC"}.
        </span>
      </Text>

      {/* CTA Button */}
      <Box mt={3}>
        <JarvisButton onClick={handleStageRedirection}>
          {activeStage?.stageNumber > 1 ? "Complete onboarding" : "Start now"}
        </JarvisButton>
      </Box>
    </>
  );
}

export default PhcOverview;
